/**
 * RESCATE DE UNA POSICIÓN DESVALORIZADA: dónde queda el borde por el que sales con tu dinero.
 *
 * El caso: entraste con 1.000 $ en una pool de Uniswap, el precio se salió por abajo y te quedaste
 * con el token volátil — hoy valen 860 $. Traes ese volátil a Keel y dices que quieres recuperar
 * 1.000. La pregunta que hay que responder ANTES de firmar es sencilla y concreta: ¿a qué precio
 * tiene que volver para que eso ocurra?
 *
 * Y tiene solución cerrada, sin iterar. Una posición de Uniswap V3 con liquidez L en [a, b] (raíces
 * de precio) vale, cuando el precio la deja entera en un lado:
 *
 *      amount0 = L·(1/a − 1/b)        amount1 = L·(b − a)
 *
 * Si entras con SOLO el volátil, el precio está pegado a uno de los bordes y el otro es el de
 * salida. Eliminando L entre las dos ecuaciones:
 *
 *   · estable = token0 (el volátil es token1, se vende según BAJA el precio hacia `a`):
 *        objetivo = amt1/(a·b)   →   a = amt1/(objetivo·b)
 *   · estable = token1 (el volátil es token0, se vende según SUBE hacia `b`):
 *        objetivo = amt0·a·b     →   b = objetivo/(amt0·a)
 *
 * Es la misma cuenta que hace el contrato en `RangeMath`, resuelta aquí para poder enseñarla antes
 * de gastar gas. Se trabaja en unidades CRUDAS (con decimales) porque así es como vive el precio de
 * Uniswap; la conversión a "cuántos estables por unidad de volátil" se hace al final.
 */

const raizDeTick = (t: number) => Math.pow(1.0001, t / 2);
const tickDeRaiz = (r: number) => Math.log(r * r) / Math.log(1.0001);

export type PlanRescate = {
  /** tick del borde por el que la posición sale convertida a estable */
  tickSalida: number;
  /** los dos bordes del rango, en estables por unidad de volátil (min < max) */
  precioMin: number;
  precioMax: number;
  /** el precio de hoy, para dibujar dónde cae dentro del rango */
  precioHoy: number;
  /** precio al que hay que volver para recuperar el objetivo, en estables por unidad de volátil */
  precioSalida: number;
  /** cuánto tiene que subir el volátil desde hoy, en % */
  subidaPct: number;
  /** ancho total de la banda, en % */
  anchoPct: number;
  /** el tope de estiramiento del vault no da para tanto: la banda se recortará */
  recortado: boolean;
  /** lo que traes, valorado a día de hoy en el estable */
  valorHoy: number;
};

export function planRescate(opciones: {
  /** tick SPOT de la pool (`slot0`): es el que usa `_recover` para colocar los bordes */
  tick: number;
  spacing: number;
  /** el estable es token0 del par */
  estableEsToken0: boolean;
  decimalesEstable: number;
  decimalesVolatil: number;
  /** cuánto volátil se trae, en unidades humanas */
  cantidadVolatil: number;
  /** cuánto ESTABLE se trae además, en unidades humanas (0 = solo volátil). NUNCA la bolsa de gas:
   *  el contrato la aparta (`gasBudget`) y no cuenta como capital. */
  cantidadEstable?: number;
  /** cuánto se quiere recuperar, en unidades humanas del estable. Es el objetivo EFECTIVO: la app
   *  apaga el colchón de fábrica antes de abrir (ver `useCrear`), así que llega tal cual al contrato. */
  objetivo: number;
  /** tope de estiramiento del vault, en ticks (`ilRecoverMaxTicks`) */
  topeTicks: number;
}): PlanRescate | null {
  // LA MISMA CUENTA QUE EL CONTRATO, NO UNA APROXIMACIÓN. Antes esto resolvía un rango "ideal" de un
  // solo lado con el borde pegado UN spacing más allá, y el contrato hace otra cosa: `_recover` pega
  // el borde al tick válido del precio, resuelve la cuadrática de `RangeMath.recoveryCeil/Floor`
  // (que incluye la pequeña venta para entrar en rango), redondea a la rejilla y topa con
  // `ilRecoverMaxTicks`. El rango que se enseñaba antes de firmar no era el que salía (2026-09-30,
  // GME/USDG 1%). Aquí se replica paso a paso, en coma flotante y unidades crudas.
  const { tick, spacing, estableEsToken0, decimalesEstable, decimalesVolatil, cantidadVolatil, objetivo, topeTicks } = opciones;
  if (!(cantidadVolatil > 0) || !(objetivo > 0) || !(spacing > 0)) return null;

  const t = Math.floor(tick);
  const piso = (x: number) => Math.floor(x / spacing) * spacing; // `_floorTick`
  const techo = (x: number) => piso(x) + spacing; // `_ceilTick`: estrictamente por encima
  const S = raizDeTick(t);
  const amtVol = cantidadVolatil * 10 ** decimalesVolatil;
  const amtEst = (opciones.cantidadEstable ?? 0) * 10 ** decimalesEstable;
  const T = objetivo * 10 ** decimalesEstable;
  const aHumano = (r: number) =>
    estableEsToken0
      ? (1 / (r * r)) * 10 ** (decimalesVolatil - decimalesEstable)
      : r * r * 10 ** (decimalesVolatil - decimalesEstable);

  let lo: number;
  let hi: number;
  let recortado: boolean;
  let valorCrudo: number;
  if (estableEsToken0) {
    // Referencia token0, volátil token1: rango POR DEBAJO; la salida es el suelo.
    hi = techo(t);
    if (raizDeTick(hi) <= S) hi += spacing;
    const Bq = raizDeTick(hi);
    const V = amtEst + amtVol / (S * S); // `recoveryFloor`: V en token0 al precio de ahora
    valorCrudo = V;
    if (V >= T) return null;
    const P = 2 * S - (S * S * (T - V)) / (T * Bq);
    const Q = (V * S * S) / T;
    const disc = P * P - 4 * Q;
    if (!(P > 0) || disc < 0) return null;
    const A = (P - Math.sqrt(disc)) / 2; // raíz menor
    if (!(A > 0) || A >= S) return null;
    let loRaw = Math.floor(tickDeRaiz(A));
    const cap = t - topeTicks;
    recortado = loRaw < cap;
    if (recortado) loRaw = cap;
    lo = piso(loRaw);
    if (lo >= hi) lo = hi - spacing;
  } else {
    // Referencia token1, volátil token0: rango POR ENCIMA; la salida es el techo.
    lo = piso(t);
    if (raizDeTick(lo) >= S) lo -= spacing;
    const A = raizDeTick(lo);
    const W = amtEst + amtVol * S * S; // `recoveryCeil`: W en token1 al precio de ahora
    valorCrudo = W;
    if (W >= T) return null;
    const P = (T * 2 * S) / W - (A * (T - W)) / W;
    const Q = (T * S * S) / W;
    const disc = P * P - 4 * Q;
    if (!(P > 0) || disc < 0) return null;
    const B = (P + Math.sqrt(disc)) / 2; // raíz mayor
    if (!(B > S)) return null;
    let hiRaw = Math.floor(tickDeRaiz(B));
    const cap = t + topeTicks;
    recortado = hiRaw > cap;
    if (recortado) hiRaw = cap;
    hi = techo(hiRaw);
    if (hi <= lo) hi = lo + spacing;
  }

  const tickSalida = estableEsToken0 ? lo : hi;
  const precioHoy = aHumano(S);
  const precioSalida = aHumano(raizDeTick(tickSalida));
  const pLo = aHumano(raizDeTick(lo));
  const pHi = aHumano(raizDeTick(hi));
  return {
    tickSalida,
    precioMin: Math.min(pLo, pHi),
    precioMax: Math.max(pLo, pHi),
    precioHoy,
    precioSalida,
    subidaPct: (precioSalida / precioHoy - 1) * 100,
    anchoPct: (Math.pow(1.0001, hi - lo) - 1) * 100,
    recortado,
    valorHoy: valorCrudo / 10 ** decimalesEstable,
  };
}
